import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';
import { FaChevronDown } from 'react-icons/fa';
import { MdCheck } from 'react-icons/md';
import { QuizRenderer } from '@/features/ai-agents/chat/QuizRenderer';
import { dispatchChatLightboxEvent } from '@/features/ai-agents/chat/chat-lightbox-event';
import { breakpoints } from '@/components/responsive';
import { ExperienceSpinner } from '@/components/experience-spinner';

interface MarkdownRendererProps {
  children?: string;
  classname?: string;
  isLatestMessage?: boolean;
  isStreamingMessage?: boolean;

  inline?: boolean;
}

interface CodeGenerationStatusProps {
  markdown: string;
  isLatestMessage: boolean;
  isStreamingMessage: boolean;
}

interface ExtractedCodeBlock {
  language: CodeLanguage;
  code: string;
  completed: boolean;
}

const CODE_LANGUAGES = [`html`, `css`, `javascript`] as const;

type CodeLanguage = typeof CODE_LANGUAGES[number];

const CODE_FENCE = String.fromCharCode(96).repeat(3);

const CODE_LABELS: Record<CodeLanguage, string> = {
  html: `Layout & Structure`,
  css: `Styles`,
  javascript: `Logic`,
};

const createStatusMap = (value = false): Record<CodeLanguage, boolean> => ({
  html: value,
  css: value,
  javascript: value,
});

const escapeForRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, `\\$&`);

const hasStartedBlock = (markdown: string, language: CodeLanguage): boolean => {
  const pattern = new RegExp(
    `${CODE_FENCE}\\s*${escapeForRegex(language)}\\b`,
    `i`,
  );
  return pattern.test(markdown);
};

const hasCompletedBlock = (
  markdown: string,
  language: CodeLanguage,
): boolean => {
  const pattern = new RegExp(
    `${CODE_FENCE}\\s*${escapeForRegex(language)}\\b[\\s\\S]*?${CODE_FENCE}`,
    `i`,
  );
  return pattern.test(markdown);
};

const getCodeBlockProgress = (
  markdown: string,
): Record<CodeLanguage, { started: boolean; completed: boolean }> => ({
  html: {
    started: hasStartedBlock(markdown, `html`),
    completed: hasCompletedBlock(markdown, `html`),
  },
  css: {
    started: hasStartedBlock(markdown, `css`),
    completed: hasCompletedBlock(markdown, `css`),
  },
  javascript: {
    started: hasStartedBlock(markdown, `javascript`),
    completed: hasCompletedBlock(markdown, `javascript`),
  },
});

const extractGeneratedCodeBlock = (
  markdown: string,
  language: CodeLanguage,
): ExtractedCodeBlock | null => {
  const pattern = new RegExp(
    `${CODE_FENCE}\\s*${escapeForRegex(
      language,
    )}\\b[^\\n]*\\n?([\\s\\S]*?)(?:${CODE_FENCE}|$)`,
    `i`,
  );
  const match = markdown.match(pattern);

  if (!match) {
    return null;
  }

  const code = (match[1] ?? ``).trimEnd();

  if (!code.length) {
    return null;
  }

  return {
    language,
    code,
    completed: hasCompletedBlock(markdown, language),
  };
};

const COMPLETE_AND_DISMISS_TIMEOUT = 500;

const CodeGenerationStatus = ({
  markdown,
  isLatestMessage,
  isStreamingMessage,
}: CodeGenerationStatusProps) => {
  const progress = useMemo(() => getCodeBlockProgress(markdown), [markdown]);
  const [dismissed, setDismissed] = useState<Record<CodeLanguage, boolean>>(
    createStatusMap(),
  );
  const [expandedMap, setExpandedMap] = useState<Record<CodeLanguage, boolean>>(
    createStatusMap(),
  );
  const scheduledRef = useRef<Set<CodeLanguage>>(new Set());

  useEffect(() => {
    if (!isLatestMessage) {
      setDismissed(createStatusMap(true));
      scheduledRef.current.clear();
      return;
    }

    setDismissed((prev) => {
      const next = { ...prev };

      CODE_LANGUAGES.forEach((language) => {
        if (!progress[language].started && !isStreamingMessage) {
          next[language] = true;
        }
      });

      return next;
    });
  }, [isLatestMessage, isStreamingMessage, progress]);

  useEffect(() => {
    CODE_LANGUAGES.forEach((language) => {
      const isCompleted =
        isLatestMessage &&
        progress[language].started &&
        progress[language].completed;

      if (isCompleted && !scheduledRef.current.has(language)) {
        scheduledRef.current.add(language);
        window.setTimeout(() => {
          setDismissed((prev) => ({ ...prev, [language]: true }));
        }, COMPLETE_AND_DISMISS_TIMEOUT);
      }
    });
  }, [isLatestMessage, progress]);

  useEffect(() => {
    setExpandedMap((prev) => {
      const next = { ...prev };

      CODE_LANGUAGES.forEach((language) => {
        if (
          !isLatestMessage ||
          dismissed[language] ||
          !progress[language].started
        ) {
          next[language] = false;
        }
      });

      return next;
    });
  }, [dismissed, isLatestMessage, progress]);

  const visibleItems = CODE_LANGUAGES.filter((language) => {
    if (!isLatestMessage) {
      return false;
    }

    if (dismissed[language]) {
      return false;
    }

    return progress[language].started;
  }).map((language) => ({
    code: extractGeneratedCodeBlock(markdown, language)?.code ?? ``,
    language,
    completed: progress[language].completed,
  }));

  if (
    progress.html.completed &&
    progress.css.completed &&
    progress.javascript.completed
  ) {
    return null;
  }

  return (
    <CodeStatusList>
      <AnimatePresence>
        {visibleItems.map(({ language, completed, code }) => (
          <CodeStatusRow
            layout
            key={language}
            initial={{ opacity: 0, y: 8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: `auto` }}
            exit={{ opacity: 0, y: -8, height: 0, marginBottom: 0 }}
            transition={{ duration: 0.28, ease: `easeOut` }}
          >
            <CodeStatusHeader
              type="button"
              onClick={() =>
                code.length
                  ? setExpandedMap((prev) => ({
                      ...prev,
                      [language]: !prev[language],
                    }))
                  : undefined
              }
            >
              <StatusText>
                {completed
                  ? `${CODE_LABELS[language]} generated`
                  : `Generating ${CODE_LABELS[language]}...`}
              </StatusText>
              <StatusRight>
                <AnimatePresence mode="wait" initial={false}>
                  {completed ? (
                    <StatusDoneIcon
                      key={`${language}-done`}
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.2, ease: `easeOut` }}
                    >
                      <MdCheck />
                    </StatusDoneIcon>
                  ) : (
                    <ExperienceSpinner size={1.5} color="#8b8b8b" />
                  )}
                </AnimatePresence>
                {!!code.length && (
                  <ChevronIcon
                    animate={{ rotate: expandedMap[language] ? 180 : 0 }}
                    transition={{ duration: 0.18, ease: `easeOut` }}
                  >
                    <FaChevronDown />
                  </ChevronIcon>
                )}
              </StatusRight>
            </CodeStatusHeader>
            <AnimatePresence initial={false}>
              {!!code.length && expandedMap[language] && (
                <CodeStatusContent
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: `auto` }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2, ease: `easeOut` }}
                >
                  <CodeStatusPre>
                    <CodeStatusValue>{code}</CodeStatusValue>
                  </CodeStatusPre>
                </CodeStatusContent>
              )}
            </AnimatePresence>
          </CodeStatusRow>
        ))}
      </AnimatePresence>
    </CodeStatusList>
  );
};

const tableComponents: Components = {
  table({ children }) {
    return (
      <TableWrapper>
        <Table>{children}</Table>
      </TableWrapper>
    );
  },
  thead({ children }) {
    return <Thead>{children}</Thead>;
  },
  tbody({ children }) {
    return <Tbody>{children}</Tbody>;
  },
  tr({ children }) {
    return <Tr>{children}</Tr>;
  },
  th({ children }) {
    return <Th>{children}</Th>;
  },
  td({ children }) {
    return <Td>{children}</Td>;
  },
};

const components: Components = {
  ...tableComponents,
  hr() {
    return <Seperator />;
  },
  code(props) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, className, node, ...rest } = props;

    if (className?.includes(`vsly/json/quiz`)) {
      return <QuizRenderer>{children}</QuizRenderer>;
    }

    if (className?.includes(`vsly/json/close`)) {
      return (
        <p style={{ fontFamily: `Eesti`, fontWeight: `bold` }}>
          You can close the chat while I’m building the experience, this might
          take a while, and the status will update automatically once it’s
          ready.
        </p>
      );
    }

    if (className?.includes(`vsly/json`)) {
      return null;
    }

    if (className?.includes(`html`)) {
      return null;
    }

    if (className?.includes(`css`)) {
      return null;
    }

    if (className?.includes(`javascript`)) {
      return null;
    }

    return (
      <code className={className} {...rest}>
        {children}
      </code>
    );
  },
  img(props) {
    return (
      <Image
        src={props.src}
        alt={props.alt}
        onClick={() => props.src && dispatchChatLightboxEvent(props.src)}
      />
    );
  },
  a(props) {
    const { href } = props;

    const handler = () => {
      if (href) {
        window.postMessage(href);
      }
    };

    const executePlan = () => {
      window.dispatchEvent(
        new CustomEvent(`ai-chat-input`, {
          detail: `Go ahead with the plan`,
        }),
      );
    };

    if (
      [`#edit-visual`, `#edit-code`].includes(href) ||
      href.includes(`#edit-strategy`)
    ) {
      return (
        <>
          <br />
          <EditButton onClick={handler}>{props.children}</EditButton>
        </>
      );
    }

    if (href?.includes(`execute-plan`)) {
      return (
        <ExecutePlanButton className="execute-plan-btn" onClick={executePlan}>
          {props.children}
        </ExecutePlanButton>
      );
    }

    return (
      <a href={props.href} target="_blank" rel="noreferrer">
        {props.children}
      </a>
    );
  },
};

export function MarkdownRenderer({
  children,
  classname,
  isLatestMessage = false,
  isStreamingMessage = false,
  inline = false,
}: MarkdownRendererProps) {
  return (
    <Wrapper>
      <ReactMarkdown
        className={classname}
        components={inline ? {} : components}
        remarkPlugins={[remarkGfm]}
        linkTarget="_blank"
      >
        {children}
      </ReactMarkdown>
      <CodeGenerationStatus
        markdown={children ?? ``}
        isLatestMessage={isLatestMessage}
        isStreamingMessage={isStreamingMessage}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  br:not(:first-child) {
    display: none;
  }
`;

const CodeStatusList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-top: 1rem;
  width: 70%;
`;

const CodeStatusRow = styled(motion.div)`
  border-radius: 1rem;
  border: 1px solid rgba(17, 17, 17, 0.12);
  background: rgba(248, 249, 251, 0.8);
  overflow: hidden;
`;

const CodeStatusHeader = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.2rem;
  cursor: pointer;
`;

const StatusRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
`;

const StatusText = styled.span`
  font-family: Eesti, sans-serif;
  font-size: 1.3rem;
  font-weight: 500;
  color: #4c5560;
`;

const StatusDoneIcon = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #3ea76a;
  font-size: 1.8rem;
  flex-shrink: 0;
`;

const ChevronIcon = styled(motion.span)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #4c5560;
  font-size: 1.1rem;
`;

const CodeStatusContent = styled(motion.div)`
  overflow: hidden;
`;

const CodeStatusPre = styled.pre`
  margin: 0;
  border-top: 1px solid rgba(17, 17, 17, 0.08);
  background: #f8f9fb;
  padding: 1.2rem;
  overflow: auto;
`;

const CodeStatusValue = styled.code`
  font-family: monospace;
  color: #111;
  font-size: 1.3rem;
  line-height: 1.45;
  white-space: pre-wrap;
`;

const EditButton = styled.button`
  appearance: none;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: black;
  border: 1px solid black;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;
  cursor: pointer;
  margin-top: 1rem;
  margin-right: 0.5rem;

  &:hover {
    background: black;
    color: white;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin: 1rem 0;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  font-family: Eesti, sans-serif;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.4rem;
`;

const Thead = styled.thead`
  background-color: rgba(0, 0, 0, 0.04);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const Tbody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const Tr = styled.tr``;

const Th = styled.th`
  padding: 1.2rem 1.6rem;
  text-align: left;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  font-size: 1.4rem;
`;

const Td = styled.td`
  padding: 1.2rem 1.6rem;
  vertical-align: top;
  color: #333;
  line-height: 1.55;
`;

const Seperator = styled.div`
  width: 80%;
  margin: 4rem auto;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
`;

const ExecutePlanButton = styled.button`
  width: 20rem;
  padding: 1.2rem 2.5rem;
  border-radius: 3rem;
  background: #111111;
  color: #fff;
  font-size: 1.4rem;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: background 0.2s;
  margin-top: 2rem;

  border: 1.5px solid rgba(0, 0, 0, 0.2);
  font-family: Eesti, sans-serif;
  font-weight: 500;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    background: #aaa;
    cursor: not-allowed;
  }

  ${breakpoints.down(`md`)} {
    max-width: 100%;
  }
`;

const Image = styled.img`
  width: auto;
  max-height: 15rem;
  object-fit: contain;
  object-position: top;
  cursor: zoom-in;
  border-radius: 0.4rem;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
`;
